import * as d3 from 'd3';
import moment, { Moment } from 'moment';
import { useMemo } from 'react';

interface ScaleInput<Domain, Range = number> {
  range: [Range, Range];
  domain: [Domain, Domain];
}

/**
 * Custom hook to generate a D3 time scale.
 *
 * @param range - The range of the scale; typically it is [0, width of the graph].
 * @param domain - The domain of the scale; [minDate, maxDate] in the time series.
 *
 * @returns A D3 time scale.
 *
 * @example
 *
 * const xScale = useTimeScale({
 *   range: [0, width],
 *   domain: [minDate, maxDate],
 * });
 */
export function useTimeScale({
  range: [rangeFrom, rangeTo],
  domain: [domainFrom, domainTo],
}: ScaleInput<Date | number | Moment | undefined>) {
  return useMemo(
    () =>
      d3
        .scaleTime()
        .domain([domainFrom ?? moment(), domainTo ?? moment()])
        .range([rangeFrom, rangeTo]),
    [domainFrom, domainTo, rangeFrom, rangeTo],
  );
}
export function useLinearScale<Range extends number | string>({
  range: [rangeFrom, rangeTo],
  domain: [domainFrom, domainTo],
}: ScaleInput<number, Range>) {
  return useMemo(
    () =>
      d3
        .scaleLinear<Range>()
        .domain([domainFrom, domainTo])
        .range([rangeFrom, rangeTo])
        .nice(),
    [domainFrom, domainTo, rangeFrom, rangeTo],
  );
}
